import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { schoolService, API_BASE_URL } from '../utils/api';
import { updateBranding } from '../redux/slices/brandingSlice';
import { useDispatch } from 'react-redux';
import { useSchool } from '../contexts/SchoolContext';
import { useUser } from '../contexts/UserContext';
import { dispatchSubdomainChanged } from '../utils/subdomainEvents';
import { useTranslation } from 'react-i18next';

// Add window location update helper
const updateLocationForSubdomainChange = (newSubdomain, oldSubdomain) => {
  try {
    // Get current path and check if it contains the old subdomain
    const currentPath = window.location.pathname;
    
    console.log('[SubdomainUpdate] Current path:', currentPath);
    console.log('[SubdomainUpdate] Old subdomain:', oldSubdomain);
    console.log('[SubdomainUpdate] New subdomain:', newSubdomain);
    
    // Construct the new path with the updated subdomain
    let newPath;
    
    if (currentPath.startsWith(`/${oldSubdomain}/`)) {
      // Replace the old subdomain with the new one in the path
      newPath = currentPath.replace(`/${oldSubdomain}/`, `/${newSubdomain}/`);
      console.log('[SubdomainUpdate] Replacing old subdomain in path:', newPath);
    } else if (currentPath === `/${oldSubdomain}`) {
      // Handle the case where the path is just the subdomain
      newPath = `/${newSubdomain}`;
      console.log('[SubdomainUpdate] Replacing old subdomain root path:', newPath);
    } else if (currentPath.includes('/admin/')) {
      // For admin paths, rebuild the path with the new subdomain
      const pathAfterAdmin = currentPath.split('/admin/')[1] || '';
      newPath = `/${newSubdomain}/admin/${pathAfterAdmin}`;
      console.log('[SubdomainUpdate] Rebuilding admin path with new subdomain:', newPath);
    } else {
      // If we can't determine the structure, just use the admin dashboard
      newPath = `/${newSubdomain}/admin`;
      console.log('[SubdomainUpdate] Using default admin path with new subdomain:', newPath);
    }
    
    // Preserve query parameters
    const queryString = window.location.search;
    const fullNewUrl = `${newPath}${queryString}`;
    
    console.log('[SubdomainUpdate] Full new URL:', fullNewUrl);
    
    // CRITICAL: Set flags to indicate this is a subdomain CHANGE, not a subdomain leaving event
    // This prevents SchoolContext from navigating to /dashboard
    sessionStorage.setItem('isSubdomainSwitching', 'true');
    sessionStorage.setItem('subdomainJustChanged', 'true');
    sessionStorage.setItem('previousSubdomain', oldSubdomain);
    sessionStorage.setItem('newSubdomain', newSubdomain);
    
    // Use navigate if available, otherwise use history API
    window.location.href = fullNewUrl;
    
    return true;
  } catch (err) {
    console.error('[SubdomainUpdate] Error updating location for subdomain change:', err);
    return false;
  }
};

/**
 * Custom hook for fetching public school settings with caching
 * Used in the public-facing menu viewer
 */
export const usePublicSchoolSettings = (schoolId) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useQuery({
    queryKey: ['publicSchoolSettings', schoolId, language],
    queryFn: async () => {
      if (!schoolId) return null;
      console.log(`[PublicSchoolSettings] Fetching settings for school ${schoolId} in language ${language}`);
      return schoolService.getPublicSchoolSettings(schoolId, language);
    },
    enabled: !!schoolId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
  });
};

/**
 * Custom hook for fetching school settings with caching
 */
export const useSchoolSettings = (schoolId) => {
  return useQuery({
    queryKey: ['schoolSettings', schoolId],
    queryFn: async () => {
      // Add timestamp parameter to prevent browser disk caching
      const timestamp = Date.now();
      console.log(`[SchoolSettingsDebug] Fetching school settings for schoolId: ${schoolId} at ${new Date(timestamp).toISOString()}`);
      
      // Custom fetch implementation to prevent browser caching
      const url = `${API_BASE_URL}/api/admin/school-settings?schoolId=${schoolId}&_t=${timestamp}`;
      console.log(`[SchoolSettingsDebug] Fetch URL: ${url}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store' // Force fetching a fresh response
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch school settings: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('[SchoolSettingsDebug] Received fresh school settings:', data);
      console.log('[SchoolSettingsDebug] show_tags value in response:', data.show_tags);
      return data;
    },
    enabled: !!schoolId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true, // Refresh when window gains focus
  });
};

/**
 * Custom hook for updating school settings with optimistic updates
 */
export const useUpdateSchoolSettings = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { refreshCurrentSchool } = useSchool();
  const { refreshUser } = useUser();

  // Helper function to directly update Navbar branding
  const updateNavbar = (settings, previousSettings) => {
    console.log('[SchoolSettingsDebug] Directly updating Navbar branding');
    dispatch(updateBranding({
      primaryBgColor: settings.primary_bg_color || previousSettings?.primary_bg_color || '#4A90E2',
      primaryTextColor: settings.primary_text_color || previousSettings?.primary_text_color || '#FFFFFF',
      secondaryBgColor: settings.secondary_bg_color || previousSettings?.secondary_bg_color || '#3498DB',
      secondaryTextColor: settings.secondary_text_color || previousSettings?.secondary_text_color || '#FFFFFF'
    }));
  };

  return useMutation({
    mutationFn: async ({ settings, schoolId }) => {
      console.log(`[SchoolSettingsDebug] Mutation triggered with:`, {
        schoolId, 
        settingsToUpdate: settings
      });
      
      // Extract subdomain from settings
      const newSubdomain = settings.subdomain;
      
      // Skip processing if no subdomain in settings
      if (!newSubdomain) {
        console.log('[SchoolSettingsDebug] No subdomain in settings update, proceeding with general update');
        return schoolService.updateSchoolSettings(settings, schoolId);
      }
      
      // Get current settings to compare
      try {
        const currentSettings = await schoolService.getSchoolSettings(schoolId);
        console.log('[SchoolSettingsDebug] Current settings retrieved:', {
          currentSubdomain: currentSettings.subdomain,
          newSubdomain: newSubdomain,
          isChanging: currentSettings.subdomain !== newSubdomain
        });
        
        // Check if subdomain is changing
        if (currentSettings.subdomain !== newSubdomain) {
          console.log(`[SchoolSettingsDebug] *** SUBDOMAIN CHANGE DETECTED *** from ${currentSettings.subdomain} to ${newSubdomain}`);
          
          // First, update the subdomain using the dedicated endpoint
          try {
            console.log(`[SchoolSettingsDebug] Calling dedicated subdomain update endpoint with: ${newSubdomain}`);
            const subdomainUpdateResult = await schoolService.updateSchoolSubdomain(newSubdomain, schoolId);
            console.log('[SchoolSettingsDebug] Subdomain update successful:', subdomainUpdateResult);
            
            // If the update only contains subdomain, we're done
            if (Object.keys(settings).length === 1 && 'subdomain' in settings) {
              console.log('[SchoolSettingsDebug] Subdomain was the only field being updated, returning result');
              return subdomainUpdateResult;
            }
            
            // Otherwise, continue with the rest of the settings
            const { subdomain, ...otherSettings } = settings;
            console.log('[SchoolSettingsDebug] Continuing with other settings update:', otherSettings);
            
            // Update the remaining settings
            return schoolService.updateSchoolSettings(otherSettings, schoolId);
          } catch (err) {
            console.error('[SchoolSettingsDebug] Error updating subdomain:', err);
            throw new Error(`Subdomain update failed: ${err.message}`);
          }
        }
      } catch (err) {
        console.error('[SchoolSettingsDebug] Error fetching current settings:', err);
      }
      
      // Fallback: proceed with general settings update (includes subdomain)
      console.log('[SchoolSettingsDebug] Proceeding with general settings update (includes subdomain)');
      return schoolService.updateSchoolSettings(settings, schoolId);
    },
    
    onMutate: async ({ settings, schoolId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['schoolSettings', schoolId],
      });

      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData(['schoolSettings', schoolId]);

      // Format the optimistic update to match the API response format
      const optimisticUpdate = {
        ...previousSettings,
        name: settings.name || previousSettings?.name,
        subdomain: settings.subdomain || previousSettings?.subdomain,
        homepage_url: settings.homepage_url || previousSettings?.homepage_url,
        primary_bg_color: settings.primary_bg_color || previousSettings?.primary_bg_color,
        primary_text_color: settings.primary_text_color || previousSettings?.primary_text_color,
        secondary_bg_color: settings.secondary_bg_color || previousSettings?.secondary_bg_color,
        secondary_text_color: settings.secondary_text_color || previousSettings?.secondary_text_color,
        default_language_id: settings.default_language_id || previousSettings?.default_language_id,
        default_menu_type_id: settings.default_menu_type_id || previousSettings?.default_menu_type_id,
        logo_path: settings.logo_path !== undefined ? settings.logo_path : previousSettings?.logo_path,
        custom_content: settings.custom_content || previousSettings?.custom_content,
        show_nutrients: settings.show_nutrients !== undefined ? settings.show_nutrients : previousSettings?.show_nutrients,
        show_tags: settings.show_tags !== undefined ? settings.show_tags : previousSettings?.show_tags,
        // Add month navigation fields
        enable_public_month_navigation: settings.enable_public_month_navigation !== undefined 
          ? settings.enable_public_month_navigation 
          : previousSettings?.enable_public_month_navigation,
        months_ahead_limit: settings.months_ahead_limit !== undefined 
          ? settings.months_ahead_limit 
          : previousSettings?.months_ahead_limit,
        months_back_limit: settings.months_back_limit !== undefined 
          ? settings.months_back_limit 
          : previousSettings?.months_back_limit,
        days_after_month_start_for_back_navigation: settings.days_after_month_start_for_back_navigation !== undefined 
          ? settings.days_after_month_start_for_back_navigation 
          : previousSettings?.days_after_month_start_for_back_navigation,
        days_before_month_end_for_forward_navigation: settings.days_before_month_end_for_forward_navigation !== undefined 
          ? settings.days_before_month_end_for_forward_navigation 
          : previousSettings?.days_before_month_end_for_forward_navigation
      };

      // Optimistically update the cache
      queryClient.setQueryData(['schoolSettings', schoolId], optimisticUpdate);
      
      // Update branding in Redux store to instantly update UI components
      // like the Navbar that use these colors
      if (settings.primary_bg_color || settings.primary_text_color || 
          settings.secondary_bg_color || settings.secondary_text_color) {
        updateNavbar(settings, previousSettings);
      }

      // Update sessionStorage cache for school data if name or subdomain changes
      if (settings.name || settings.subdomain) {
        try {
          const cachedSchool = JSON.parse(sessionStorage.getItem('currentSchool') || '{}');
          if (cachedSchool && cachedSchool.id === schoolId) {
            // Update the cached school data with new values
            const updatedCache = {
              ...cachedSchool,
              name: settings.name || cachedSchool.name,
              subdomain: settings.subdomain || cachedSchool.subdomain,
              primary_bg_color: settings.primary_bg_color || cachedSchool.primary_bg_color,
              primary_text_color: settings.primary_text_color || cachedSchool.primary_text_color,
              secondary_bg_color: settings.secondary_bg_color || cachedSchool.secondary_bg_color,
              secondary_text_color: settings.secondary_text_color || cachedSchool.secondary_text_color
            };
            sessionStorage.setItem('currentSchool', JSON.stringify(updatedCache));
            console.log('[useUpdateSchoolSettings] Updated school in sessionStorage cache');
          }
          
          // CRITICAL FIX: Immediately update the user's schools list in localStorage
          // to prevent access issues due to stale data
          if (settings.subdomain && previousSettings?.subdomain !== settings.subdomain) {
            try {
              const userData = JSON.parse(localStorage.getItem('user') || '{}');
              if (userData && userData.schools && Array.isArray(userData.schools)) {
                // Find the school in the user's schools list
                const schoolIndex = userData.schools.findIndex(s => s.id === schoolId || s.subdomain === previousSettings.subdomain);
                if (schoolIndex !== -1) {
                  // Update the subdomain in the user's schools list
                  userData.schools[schoolIndex].subdomain = settings.subdomain;
                  
                  // Also update current_school if it's the same school
                  if (userData.current_school && (userData.current_school.id === schoolId || 
                      userData.current_school.subdomain === previousSettings.subdomain)) {
                    userData.current_school.subdomain = settings.subdomain;
                    if (settings.name) {
                      userData.current_school.name = settings.name;
                    }
                  }
                  
                  // Save the updated user data back to localStorage
                  localStorage.setItem('user', JSON.stringify(userData));
                  console.log('[useUpdateSchoolSettings] Updated user schools list in localStorage with new subdomain', 
                            previousSettings.subdomain, '->', settings.subdomain);
                }
              }
            } catch (err) {
              console.error('[useUpdateSchoolSettings] Error updating user schools in localStorage:', err);
            }
          }
        } catch (err) {
          console.error('[useUpdateSchoolSettings] Error updating sessionStorage:', err);
        }
      }

      return { previousSettings };
    },
    
    onError: (err, { schoolId, settings }, context) => {
      // If the mutation fails, revert to the previous value
      if (context?.previousSettings) {
        queryClient.setQueryData(
          ['schoolSettings', schoolId],
          context.previousSettings
        );
        
        // Also restore the original branding in Redux
        const prev = context.previousSettings;
        if (prev) {
          updateNavbar(prev, null);
        }
      }
    },
    
    onSettled: async (data, error, { schoolId, settings }, context) => {
      // Refetch to ensure the local data is in sync with the server
      console.log(`[SchoolSettingsDebug] onSettled called after mutation, invalidating caches - success: ${!error}`);
      
      // Invalidate admin school settings
      queryClient.invalidateQueries({
        queryKey: ['schoolSettings', schoolId],
      });
      
      // Invalidate current school data in SchoolContext queries if used
      queryClient.invalidateQueries({
        queryKey: ['currentSchool'],
      });
      
      // Invalidate schoolBySubdomain cache 
      if (data?.subdomain) {
        console.log(`[SchoolSettingsDebug] Invalidating schoolBySubdomain cache for ${data.subdomain}`);
        queryClient.invalidateQueries({
          queryKey: ['schoolBySubdomain', data.subdomain],
        });
      }
      
      // Invalidate public school settings cache used by the PublicMenuViewer
      console.log(`[SchoolSettingsDebug] Invalidating public school settings cache for schoolId ${schoolId}`);
      queryClient.invalidateQueries({
        predicate: (query) => 
          query.queryKey[0] === 'publicSchoolSettings' && 
          query.queryKey.includes(schoolId)
      });
      
      // If public month navigation settings changed, invalidate any public menu data
      if (settings.enable_public_month_navigation !== undefined ||
          settings.months_ahead_limit !== undefined ||
          settings.months_back_limit !== undefined ||
          settings.days_after_month_start_for_back_navigation !== undefined ||
          settings.days_before_month_end_for_forward_navigation !== undefined) {
        
        console.log('[SchoolSettingsDebug] Month navigation settings changed, invalidating publicMenu cache');
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'publicMenu',
        });
      }
      
      // If the mutation was successful, refresh necessary data
      if (!error && data) {
        try {
          console.log('[SchoolSettingsDebug] Explicitly refreshing context and queries after successful settings update');
          
          // Refresh SchoolContext data
          await refreshCurrentSchool();
          
          // Force refetch of admin schoolSettings query
          await queryClient.refetchQueries({
            queryKey: ['schoolSettings', schoolId],
            exact: true
          });
          
          // Force refetch of public schoolSettings query
          await queryClient.refetchQueries({
            predicate: (query) => 
              query.queryKey[0] === 'publicSchoolSettings' && 
              query.queryKey.includes(schoolId),
            exact: true
          });
          
          // Refresh user data
          await refreshUser();
          
          // Handle subdomain change navigation (if applicable)
          if (settings.subdomain && context?.previousSettings?.subdomain && 
              settings.subdomain !== context.previousSettings.subdomain) {
            
            const currentPath = window.location.pathname;
            console.log(`[SchoolSettingsDebug] Subdomain changed from ${context.previousSettings.subdomain} to ${settings.subdomain}`);
            console.log(`[SchoolSettingsDebug] Setting preventNavigation flag and updating URL`);
            
            dispatchSubdomainChanged(
              settings.subdomain,
              context.previousSettings.subdomain,
              { 
                preventNavigation: true,
                currentPath
              }
            );
            
            const newUrl = window.location.href.replace(
              `/${context.previousSettings.subdomain}/`, 
              `/${settings.subdomain}/`
            );
            window.history.pushState({}, '', newUrl);
          }
        } catch (err) {
          console.error('[SchoolSettingsDebug] Error in post-mutation refresh:', err);
        }
      }
    },
  });
}; 